<template>
  <div class="canvas-viewer" ref="containerRef">
    <svg
      ref="svgRef"
      class="canvas-svg"
      width="100%"
      height="100%"
      :viewBox="viewBoxString"
      preserveAspectRatio="xMidYMid meet"
      @mousedown="handleMouseDown"
      @mouseup="handleMouseUp"
      @mouseleave="handleMouseUp"
      @wheel.prevent="handleWheel"
      @contextmenu.prevent="handleContextMenu"
      @touchstart="handleTouchStart"
      @touchmove="handleTouchMove"
      @touchend="handleTouchEnd"
    >
      <!-- Grille de fond -->
      <defs>
        <pattern
          id="grid"
          width="200"
          height="200"
          patternUnits="userSpaceOnUse"
        >
          <path
            d="M 200 0 L 0 0 0 200"
            fill="none"
            stroke="var(--cv-grid-stroke)"
            stroke-width="12"
          />
        </pattern>
      </defs>
      <!-- Fond et grille infinis (très grande surface) -->
      <rect
        data-canvas-base="true"
        x="-50000"
        y="-50000"
        width="100000"
        height="100000"
        fill="var(--cv-canvas-fill)"
      />
      <rect x="-50000" y="-50000" width="100000" height="100000" fill="url(#grid)" />

      <!-- Blocs sur le canvas -->
      <BlockInstance
        v-for="blockInstance in sortedBlocks"
        :key="blockInstance.id"
        :block-instance="blockInstance"
        :selected="selectedBlockIds.includes(blockInstance.id)"
        :hide-rotation-handle="isBlockInSelectedGroup(blockInstance.id)"
        :show-z-order="showZOrderIndicators"
        :svg-ref="svgRef"
        :zoom-level="zoomLevel"
        :pan-offset="panOffset"
        :canvas-pixels-per-user="canvasPixelsPerUser"
        @select="(id, e) => handleSelectBlock(id, e)"
        @update="(id, updates, save) => handleUpdateBlock(id, updates, save)"
        @z-order-up="handleZOrderUp"
        @z-order-down="handleZOrderDown"
        @delete="handleDeleteBlock"
      />

      <!-- Bounding box des groupes dont au moins un bloc est sélectionné -->
      <g v-if="selectedGroupBBoxes.length" class="group-bbox-layer" style="pointer-events: none;">
        <rect
          v-for="(box, i) in selectedGroupBBoxes"
          :key="i"
          :x="box.x"
          :y="box.y"
          :width="box.width"
          :height="box.height"
          fill="none"
          stroke="var(--cv-accent)"
          :stroke-width="GROUP_BBOX_STROKE_WIDTH"
          stroke-dasharray="8 4"
          opacity="0.9"
        />
      </g>
      <!-- Poignée de rotation du groupe : comme BlockInstance (centre haut de la bbox, taille écran constante) -->
      <g
        v-if="groupRotateHandleLayout"
        class="group-rotation-handle"
        @mousedown.stop="handleGroupRotateStart"
      >
        <circle
          :cx="groupRotateHandleLayout.cx"
          :cy="groupRotateHandleLayout.cy"
          :r="groupRotateHandleLayout.r"
          fill="var(--cv-accent)"
          stroke="white"
          :stroke-width="groupRotateHandleLayout.strokeW"
        />
        <text
          :x="groupRotateHandleLayout.cx"
          :y="groupRotateHandleLayout.cy"
          :font-size="groupRotateHandleLayout.iconFont"
          text-anchor="middle"
          dominant-baseline="middle"
          fill="white"
          pointer-events="none"
        >↻</text>
      </g>

      <!-- Annotations (masquables temporairement) -->
      <g v-if="!annotationsTemporarilyHidden">
        <!-- Annotations de type label -->
        <LabelAnnotation
          v-for="annotation in labelAnnotations"
          :key="annotation.id"
          :annotation="annotation"
          :selected="selectedAnnotationId === annotation.id"
          :svg-ref="svgRef"
          @select="handleSelectAnnotation"
          @update="handleUpdateAnnotation"
          @delete="handleDeleteAnnotation"
          @move="handleMoveAnnotation"
        />

        <!-- Annotations de type ligne -->
        <LineAnnotation
          v-for="annotation in lineAnnotations"
          :key="annotation.id"
          :annotation="annotation"
          :selected="selectedAnnotationId === annotation.id"
          :svg-ref="svgRef"
          :canvas-pixels-per-user="canvasPixelsPerUser"
          @select="handleSelectAnnotation"
          @update="handleUpdateAnnotation"
          @delete="handleDeleteAnnotation"
        />

        <!-- Annotations de type rectangle -->
        <RectangleAnnotation
          v-for="annotation in rectangleAnnotations"
          :key="annotation.id"
          :annotation="annotation"
          :selected="selectedAnnotationId === annotation.id"
          :svg-ref="svgRef"
          :canvas-pixels-per-user="canvasPixelsPerUser"
          @select="handleSelectAnnotation"
          @update="handleUpdateAnnotation"
          @delete="handleDeleteAnnotation"
        />

        <!-- Annotations de type cercle -->
        <CircleAnnotation
          v-for="annotation in circleAnnotations"
          :key="annotation.id"
          :annotation="annotation"
          :selected="selectedAnnotationId === annotation.id"
          :svg-ref="svgRef"
          :canvas-pixels-per-user="canvasPixelsPerUser"
          @select="handleSelectAnnotation"
          @update="handleUpdateAnnotation"
          @delete="handleDeleteAnnotation"
        />

        <!-- Annotations de type mesure -->
        <MeasureAnnotation
          v-for="annotation in measureAnnotations"
          :key="annotation.id"
          :annotation="annotation"
          :selected="selectedAnnotationId === annotation.id"
          :svg-ref="svgRef"
          :canvas-pixels-per-user="canvasPixelsPerUser"
          :scale="referenceScale"
          @select="handleSelectAnnotation"
          @update="handleUpdateAnnotation"
          @delete="handleDeleteAnnotation"
        />
      </g>

      <!-- Zone de drop au premier plan (pendant un drag depuis la bibliothèque) -->
      <rect
        v-if="isDraggingFromLibrary"
        x="-50000"
        y="-50000"
        width="100000"
        height="100000"
        fill="transparent"
        style="pointer-events: all;"
        @drop="handleDrop"
        @dragover.prevent
        @dragenter.prevent
      />
    </svg>

    <div
      v-if="selectedBlockDimensionsLabel"
      class="canvas-selection-dims"
      aria-live="polite"
    >
      <span class="dims-name">{{ selectedBlockDimensionsLabel.name }}</span>
      <span v-if="selectedBlockDimensionsLabel.identification" class="dims-meta">{{
        selectedBlockDimensionsLabel.identification
      }}</span>
      <span v-if="selectedBlockDimensionsLabel.poidsLine" class="dims-meta dims-meta--poids">{{
        selectedBlockDimensionsLabel.poidsLine
      }}</span>
      <span v-if="selectedBlockDimensionsLabel.mmLine" class="dims-size">
        {{ selectedBlockDimensionsLabel.mmLine }}
      </span>
      <span v-else class="dims-size dims-size--pending">Dimensions non renseignées (édition du bloc)</span>
    </div>

    <!-- Contrôles de zoom -->
    <div class="canvas-controls" @mousedown.stop @click.stop>
      <div class="zoom-controls">
        <button @click.stop="zoomIn" class="control-btn" title="Zoomer (Ctrl++)">
          <span class="material-symbols-outlined">zoom_in</span>
        </button>
        <span class="zoom-level">{{ Math.round(safeZoomLevel * 100) }}%</span>
        <button @click.stop="zoomOut" class="control-btn" title="Dézoomer (Ctrl+-)">
          <span class="material-symbols-outlined">zoom_out</span>
        </button>
        <button @click.stop="frameAll" class="control-btn" title="Cadrer tous les blocs (Ctrl+0)">
          <span class="material-symbols-outlined">fit_screen</span>
        </button>
        <button
          @click.stop="showZOrderIndicators = !showZOrderIndicators"
          :class="['control-btn', 'z-order-toggle', { active: showZOrderIndicators }]"
          title="Afficher les indicateurs de profondeur"
        >
          <span class="material-symbols-outlined">layers</span>
        </button>
        <button
          @click.stop="selectedBlockIds.length >= 2 && handleCreateGroup()"
          class="control-btn"
          :class="{ 'control-btn-disabled': selectedBlockIds.length < 2 }"
          :title="selectedBlockIds.length >= 2 ? 'Grouper la sélection' : 'Grouper (sélectionnez au moins 2 blocs)'"
        >
          <span class="material-symbols-outlined">group</span>
        </button>
        <button
          @click.stop="canUngroupSelection && handleUngroupSelection()"
          class="control-btn"
          :class="{ 'control-btn-disabled': !canUngroupSelection }"
          :title="canUngroupSelection ? 'Dissocier la sélection' : 'Dissocier (sélectionnez des blocs dans un groupe)'"
        >
          <span class="material-symbols-outlined">ungroup</span>
        </button>
        <button
          type="button"
          @click.stop="toggleTheme"
          class="control-btn"
          :title="isLightMode ? 'Activer le mode sombre' : 'Activer le mode clair'"
        >
          <span class="material-symbols-outlined">{{ isLightMode ? 'dark_mode' : 'light_mode' }}</span>
        </button>
      </div>
    </div>

    <ConfirmModal
      :show="showDeleteConfirm"
      :title="blocksToDelete.length > 1 ? 'Supprimer les blocs' : 'Supprimer le bloc'"
      :message="blocksToDelete.length > 1 ? 'Êtes-vous sûr de vouloir retirer les blocs sélectionnés du canvas ?' : 'Êtes-vous sûr de vouloir retirer ce bloc du canvas ?'"
      confirm-label="Supprimer"
      :danger="true"
      @confirm="confirmDelete"
      @cancel="cancelDelete"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useHypothesesStore } from '@stores/hypotheses.store';
import { useBlocsStore } from '@stores/blocs.store';
import BlockInstance from './BlockInstance.vue';
import ConfirmModal from './ConfirmModal.vue';
import LabelAnnotation from './LabelAnnotation.vue';
import LineAnnotation from './LineAnnotation.vue';
import RectangleAnnotation from './RectangleAnnotation.vue';
import CircleAnnotation from './CircleAnnotation.vue';
import MeasureAnnotation from './MeasureAnnotation.vue';
import { annotationsApi, type Annotation } from '@api/annotations.api';
import type { CanvasGroup, HypothesisBlock } from '@api/hypotheses.api';
import {
  EXPORT_MAX_SIDE,
  EXPORT_RASTER_MULTIPLIER,
  REFERENCE_SCALE_PX_PER_MM,
} from '@/constants/canvas';
import { inlineSvgRasterImages } from '@/utils/inlineSvgImagesForExport';
import {
  annotationMmToCanvasPx,
  ANNOTATION_DEFAULT_LINE_LENGTH_MM,
  ANNOTATION_DEFAULT_CIRCLE_DIAMETER_MM,
  ANNOTATION_DEFAULT_RECT_WIDTH_MM,
  ANNOTATION_DEFAULT_RECT_HEIGHT_MM,
  ANNOTATION_DEFAULT_STROKE_PX,
} from '@/constants/annotations';
import { useBlockDimensionsCache } from '../composables/useBlockDimensionsCache';
import { useBlockAlphaCache } from '../composables/useBlockAlphaCache';
import {
  THEME_STORAGE_KEY,
  readStoredTheme,
  applyThemeToBody,
  type ThemeId,
} from '@/utils/theme';

const hypothesesStore = useHypothesesStore();
const blocsStore = useBlocsStore(); // fallback tailles mm si nested bloc hypothèse désynchronisé
const containerRef = ref<HTMLElement | null>(null);
const svgRef = ref<SVGSVGElement | null>(null);
const alphaCache = useBlockAlphaCache();
const isLightMode = ref(readStoredTheme() === 'light');

function toggleTheme() {
  const newTheme: ThemeId = isLightMode.value ? 'dark' : 'light';
  isLightMode.value = newTheme === 'light';
  localStorage.setItem(THEME_STORAGE_KEY, newTheme);
  applyThemeToBody(newTheme);
}

/** Convertit des coordonnées SVG en coordonnées locales de l'image d'un bloc (annule translation + rotation). */
function svgToBlockLocalCoords(svgX: number, svgY: number, block: HypothesisBlock, w: number, h: number): { x: number; y: number } {
  const dx = svgX - Number(block.position_x);
  const dy = svgY - Number(block.position_y);
  const rot = block.rotation ?? 0;
  const cx = w / 2;
  const cy = h / 2;
  const rad = (-rot * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  return {
    x: cos * (dx - cx) - sin * (dy - cy) + cx,
    y: sin * (dx - cx) + cos * (dy - cy) + cy,
  };
}

/**
 * Cherche le bloc opaque le plus au-dessus sous le curseur SVG (svgX, svgY).
 * Parcourt les blocs par z-order décroissant (dessus en premier).
 * Retourne l'id du bloc trouvé, ou null si aucun.
 */
function hitTestBlocks(svgX: number, svgY: number, excludeBlockId?: number): number | null {
  const sorted = [...blocks.value].sort((a, b) => b.z_order - a.z_order);
  for (const block of sorted) {
    if (block.id === excludeBlockId) continue;
    const { width: w, height: h } = getBlockCanvasDimensions(block);
    const local = svgToBlockLocalCoords(svgX, svgY, block, w, h);
    const blocId = block.bloc?.id ?? block.bloc_id;
    const opaque = alphaCache.isOpaque(blocId, local.x, local.y);
    if (opaque === true) return block.id;
    // opaque === null : image pas encore chargée, on considère comme opaque si dans les bounds
    if (opaque === null && local.x >= 0 && local.y >= 0 && local.x < w && local.y < h) return block.id;
  }
  return null;
}

/** Retourne les blocs touchés au point, du dessus vers le dessous. */
function hitTestBlocksStack(svgX: number, svgY: number, excludeBlockId?: number): number[] {
  const sorted = [...blocks.value].sort((a, b) => b.z_order - a.z_order);
  const hits: number[] = [];
  for (const block of sorted) {
    if (block.id === excludeBlockId) continue;
    const { width: w, height: h } = getBlockCanvasDimensions(block);
    const local = svgToBlockLocalCoords(svgX, svgY, block, w, h);
    const blocId = block.bloc?.id ?? block.bloc_id;
    const opaque = alphaCache.isOpaque(blocId, local.x, local.y);
    if (opaque === true) {
      hits.push(block.id);
      continue;
    }
    if (opaque === null && local.x >= 0 && local.y >= 0 && local.x < w && local.y < h) {
      hits.push(block.id);
    }
  }
  return hits;
}
// Taille monde fixe (indépendante du conteneur) mais grande : zoom/pan possibles, place pour beaucoup de blocs
const CANVAS_WORLD_WIDTH = 10000;
const CANVAS_WORLD_HEIGHT = 10000;
const selectedBlockIds = ref<number[]>([]);
const zoomLevel = ref(1);
const isPanning = ref(false);
const panStart = ref({ x: 0, y: 0 });
const panOffset = ref({ x: 0, y: 0 });
const showZOrderIndicators = ref(false);
const touchStartPos = ref<{ x: number; y: number } | null>(null);
const blockDimensionsByBlocId = ref<Map<number, { width: number; height: number }>>(new Map());
const blockDimensionsCache = useBlockDimensionsCache();
/** Même logique que BlockInstance : carte live → cache session → métadonnées API → défaut. */
function getBlockCanvasDimensions(block: HypothesisBlock): { width: number; height: number } {
  const blocId = block.bloc?.id ?? block.bloc_id;
  if (blocId != null) {
    const fromLive = blockDimensionsByBlocId.value.get(blocId);
    if (fromLive) return fromLive;
    const fromCache = blockDimensionsCache.get(blocId, block.bloc?.updated_at);
    if (fromCache) return fromCache;
  }
  const w = block.bloc?.image_width;
  const h = block.bloc?.image_height;
  if (typeof w === 'number' && typeof h === 'number' && w > 0 && h > 0) {
    return { width: w, height: h };
  }
  return { width: 200, height: 200 };
}
const canvasRectRef = ref({ width: 0, height: 0 });
const isDraggingFromLibrary = ref(false);
const activeAnnotationTool = ref<string | null>(null);
const selectedAnnotationId = ref<number | null>(null);
const showDeleteConfirm = ref(false);
const blocksToDelete = ref<number[]>([]);
const annotationsTemporarilyHidden = ref(false);

const ZOOM_MIN = 0.1;   // 10%
const ZOOM_MAX = 30;   // 3000%
const clampZoom = (z: number): number => {
  const n = Number(z);
  if (n !== n || n < ZOOM_MIN) return ZOOM_MIN;
  if (n > ZOOM_MAX) return ZOOM_MAX;
  return n;
};

// Throttle sauvegarde canvas : max 1 requête / seconde
const SAVE_THROTTLE_MS = 2000;
let lastSaveTime = 0;
let saveTimeoutId: ReturnType<typeof setTimeout> | null = null;
let pendingBlocksToSave: any[] | null = null;
let canvasSaveRetryTimeoutId: ReturnType<typeof setTimeout> | null = null;
let canvasSaveRetryDelayMs = 1500;
const CANVAS_SAVE_RETRY_MAX_MS = 10000;

const ANNOTATION_SAVE_THROTTLE_MS = 400;
const ANNOTATION_RETRY_BASE_MS = 1200;
const ANNOTATION_RETRY_MAX_MS = 10000;
let annotationSaveTimeoutId: ReturnType<typeof setTimeout> | null = null;
let annotationRetryTimeoutId: ReturnType<typeof setTimeout> | null = null;
let annotationRetryDelayMs = ANNOTATION_RETRY_BASE_MS;
const pendingAnnotationSaves = new Map<number, { annotation: Annotation; data: any }>();
let apiCallWindowStart = Date.now();
let apiCallsInWindow = 0;

function trackApiCall(endpoint: string, action: string, status?: number) {
  const now = Date.now();
  if (now - apiCallWindowStart >= 1000) {
    if (import.meta.env.DEV && apiCallsInWindow > 0) {
      console.debug('[CanvasViewer] API calls/sec', { callsPerSec: apiCallsInWindow });
    }
    apiCallWindowStart = now;
    apiCallsInWindow = 0;
  }
  apiCallsInWindow += 1;
  if (import.meta.env.DEV) {
    console.debug('[CanvasViewer] API call', { action, endpoint, status });
  }
}

async function flushPendingAnnotationSaves() {
  if (pendingAnnotationSaves.size === 0) return;
  const batch = Array.from(pendingAnnotationSaves.values());
  pendingAnnotationSaves.clear();

  const failed: Array<{ annotation: Annotation; data: any }> = [];
  for (const item of batch) {
    try {
      trackApiCall(`/api/annotations/${item.annotation.id}`, 'annotation.update');
      await annotationsApi.update(item.annotation.id, { data: item.data });
    } catch (error: any) {
      const status = error?.response?.status;
      trackApiCall(`/api/annotations/${item.annotation.id}`, 'annotation.update.error', status);
      if (status === 429 && import.meta.env.DEV) {
        console.warn('[CanvasViewer] 429 on annotation update', { annotationId: item.annotation.id });
      }
      failed.push(item);
    }
  }

  if (failed.length === 0) {
    annotationRetryDelayMs = ANNOTATION_RETRY_BASE_MS;
    if (annotationRetryTimeoutId !== null) {
      clearTimeout(annotationRetryTimeoutId);
      annotationRetryTimeoutId = null;
    }
    return;
  }

  for (const item of failed) {
    pendingAnnotationSaves.set(item.annotation.id, item);
  }

  if (annotationRetryTimeoutId === null) {
    annotationRetryTimeoutId = setTimeout(() => {
      annotationRetryTimeoutId = null;
      void flushPendingAnnotationSaves();
    }, annotationRetryDelayMs);
    annotationRetryDelayMs = Math.min(annotationRetryDelayMs * 2, ANNOTATION_RETRY_MAX_MS);
  }
}

function scheduleAnnotationSave(saveNow = false) {
  if (saveNow) {
    if (annotationSaveTimeoutId !== null) {
      clearTimeout(annotationSaveTimeoutId);
      annotationSaveTimeoutId = null;
    }
    void flushPendingAnnotationSaves();
    return;
  }

  if (annotationSaveTimeoutId !== null) return;
  annotationSaveTimeoutId = setTimeout(() => {
    annotationSaveTimeoutId = null;
    void flushPendingAnnotationSaves();
  }, ANNOTATION_SAVE_THROTTLE_MS);
}

const safeZoomLevel = computed(() => clampZoom(zoomLevel.value));
const viewBoxString = computed(() => {
  const z = safeZoomLevel.value;
  const px = Number.isFinite(panOffset.value.x) ? panOffset.value.x : 0;
  const py = Number.isFinite(panOffset.value.y) ? panOffset.value.y : 0;
  return `${-px / z} ${-py / z} ${CANVAS_WORLD_WIDTH / z} ${CANVAS_WORLD_HEIGHT / z}`;
});

const currentHypothesis = computed(() => hypothesesStore.currentHypothesis);
const blocks = computed(() => currentHypothesis.value?.blocks || []);

const sortedBlocks = computed(() => {
  return [...blocks.value].sort((a, b) => a.z_order - b.z_order);
});

function parseLocaleMm(v: unknown): number | null {
  if (v == null || v === '') return null;
  if (typeof v === 'number') return Number.isFinite(v) && v >= 0 ? v : null;
  const s = String(v).trim().replace(/\s/g, '').replace(',', '.');
  const n = parseFloat(s);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

function formatMmValue(v: unknown): string | null {
  const n = parseLocaleMm(v);
  if (n == null) return null;
  return n.toLocaleString('fr-FR', { maximumFractionDigits: 1, minimumFractionDigits: 0 });
}

/** Une seule instance sélectionnée : nom + dimensions réelles X×Y×Z (mm) depuis la base. */
const selectedBlockDimensionsLabel = computed(() => {
  if (selectedBlockIds.value.length !== 1) return null;
  const hb = blocks.value.find((b) => b.id === selectedBlockIds.value[0]);
  const bloc = hb?.bloc;
  if (!bloc) return null;
  const lib = blocsStore.blocs.find((b) => b.id === bloc.id);
  const rx = lib?.real_size_x_mm ?? bloc.real_size_x_mm;
  const ry = lib?.real_size_y_mm ?? bloc.real_size_y_mm;
  const rz = lib?.real_size_z_mm ?? bloc.real_size_z_mm;
  const sx = formatMmValue(rx);
  const sy = formatMmValue(ry);
  const sz = formatMmValue(rz);
  let mmLine: string | null = null;
  if (sx && sy && sz) {
    mmLine = `${sx} × ${sy} × ${sz} mm (X × Y × Z)`;
  } else if (sx || sy || sz) {
    const parts = [
      sx ? `X ${sx}` : 'X —',
      sy ? `Y ${sy}` : 'Y —',
      sz ? `Z ${sz}` : 'Z —',
    ];
    mmLine = `${parts.join(' · ')} mm`;
  }
  const identification =
    (lib?.fragment_identification ?? bloc.fragment_identification)?.trim() || null;
  const poidsRaw = lib?.poids_estime_kg ?? bloc.poids_estime_kg;
  const poidsLine =
    poidsRaw != null && Number.isFinite(Number(poidsRaw))
      ? `Poids estimé : ${Number(poidsRaw)} kg`
      : null;

  return {
    name: bloc.object_name ?? '—',
    identification,
    poidsLine,
    mmLine,
  };
});

const allAnnotations = computed(() => {
  return currentHypothesis.value?.annotations || [];
});

// Visibilité "en dur" : afficher seulement si visible est truthy (gère boolean et 0/1 MySQL)
const isAnnotationVisible = (a: { visible?: boolean | number }) =>
  a.visible !== false && a.visible !== 0;

const labelAnnotations = computed(() => {
  return allAnnotations.value.filter(a => a.type === 'label' && isAnnotationVisible(a));
});

const lineAnnotations = computed(() => {
  return allAnnotations.value.filter(a => a.type === 'line' && isAnnotationVisible(a));
});

const rectangleAnnotations = computed(() => {
  return allAnnotations.value.filter(a => a.type === 'rectangle' && isAnnotationVisible(a));
});

const circleAnnotations = computed(() => {
  return allAnnotations.value.filter(a => a.type === 'circle' && isAnnotationVisible(a));
});

const measureAnnotations = computed(() => {
  return allAnnotations.value.filter(a => a.type === 'measure' && isAnnotationVisible(a));
});

// Échelle de référence constante pour toute la plateforme (px/mm).
const referenceScale = REFERENCE_SCALE_PX_PER_MM;

const groups = computed(() => currentHypothesis.value?.groups ?? []);

function getGroupBlockIds(groupId: string): number[] {
  const g = groups.value.find((x) => x.id === groupId);
  if (!g) return [];
  const ids = [...g.blockIds];
  for (const cid of g.groupIds) {
    ids.push(...getGroupBlockIds(cid));
  }
  return ids;
}

function getGroupContainingBlock(blockId: number): CanvasGroup | null {
  return groups.value.find((g) => g.blockIds.includes(blockId)) ?? null;
}

const GROUP_BBOX_PADDING = 12;
const GROUP_BBOX_STROKE_WIDTH = 10;
/** Mêmes valeurs que BlockInstance.vue (poignée rotation bloc seul) */
const GROUP_ROTATE_HANDLE_OFFSET_USER = 25;
const GROUP_CONTROL_RADIUS_PX = 10;
const GROUP_CONTROL_STROKE_PX = 1.5;
const GROUP_CONTROL_ICON_PX = 12;

/** Ramène l'angle en degrés dans [0, 360) pour éviter accumulation 360°, 720°, etc. */
function normalizeAngleDeg(deg: number): number {
  const d = Number(deg) || 0;
  if (!Number.isFinite(d)) return 0;
  const r = d % 360;
  return r < 0 ? r + 360 : r;
}

function rotatePoint(px: number, py: number, cx: number, cy: number, angleDeg: number) {
  const rad = (angleDeg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const dx = px - cx;
  const dy = py - cy;
  return { x: cx + dx * cos - dy * sin, y: cy + dx * sin + dy * cos };
}

/** Boîte englobante AABB d'un bloc image (coin haut-gauche + taille), avec rotation autour du centre. */
function getHypothesisBlockRotatedAabb(block: HypothesisBlock): {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
} {
  const { width: w, height: h } = getBlockCanvasDimensions(block);
  const px = Number(block.position_x);
  const py = Number(block.position_y);
  const cx = px + w / 2;
  const cy = py + h / 2;
  const rot = normalizeAngleDeg(Number(block.rotation) || 0);
  const corners = [
    rotatePoint(px, py, cx, cy, rot),
    rotatePoint(px + w, py, cx, cy, rot),
    rotatePoint(px + w, py + h, cx, cy, rot),
    rotatePoint(px, py + h, cx, cy, rot),
  ];
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of corners) {
    minX = Math.min(minX, p.x);
    minY = Math.min(minY, p.y);
    maxX = Math.max(maxX, p.x);
    maxY = Math.max(maxY, p.y);
  }
  return { minX, minY, maxX, maxY };
}

function getGroupCornersBounds(
  groupId: string
): { minX: number; maxX: number; minY: number; maxY: number } | null {
  const ids = getGroupBlockIds(groupId);
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const id of ids) {
    const b = blocks.value.find((x) => x.id === id);
    if (!b) continue;
    const box = getHypothesisBlockRotatedAabb(b);
    minX = Math.min(minX, box.minX);
    minY = Math.min(minY, box.minY);
    maxX = Math.max(maxX, box.maxX);
    maxY = Math.max(maxY, box.maxY);
  }
  if (minX === Infinity) return null;
  return { minX, maxX, minY, maxY };
}

function getGroupAxisAlignedBBox(
  groupId: string
): { x: number; y: number; width: number; height: number } | null {
  const b = getGroupCornersBounds(groupId);
  if (!b) return null;
  return {
    x: b.minX - GROUP_BBOX_PADDING,
    y: b.minY - GROUP_BBOX_PADDING,
    width: b.maxX - b.minX + 2 * GROUP_BBOX_PADDING,
    height: b.maxY - b.minY + 2 * GROUP_BBOX_PADDING,
  };
}

const selectedGroupBBoxes = computed(() => {
  const boxes: { x: number; y: number; width: number; height: number }[] = [];
  const seenGroupIds = new Set<string>();
  for (const blockId of selectedBlockIds.value) {
    const group = getGroupContainingBlock(blockId);
    if (!group || seenGroupIds.has(group.id)) continue;
    seenGroupIds.add(group.id);
    const box = getGroupAxisAlignedBBox(group.id);
    if (box) boxes.push(box);
  }
  return boxes;
});

function getGroupBBoxCenter(groupId: string): { x: number; y: number } | null {
  const b = getGroupCornersBounds(groupId);
  if (!b) return null;
  return { x: (b.minX + b.maxX) / 2, y: (b.minY + b.maxY) / 2 };
}

const canUngroupSelection = computed(() =>
  selectedBlockIds.value.some((id) => getGroupContainingBlock(id) != null)
);

function isBlockInSelectedGroup(blockId: number): boolean {
  const group = getGroupContainingBlock(blockId);
  if (!group) return false;
  const ids = getGroupBlockIds(group.id);
  return ids.some((id) => selectedBlockIds.value.includes(id));
}

const selectedGroupCenter = computed(() => {
  const blockId = selectedBlockIds.value[0];
  if (blockId == null) return null;
  const group = getGroupContainingBlock(blockId);
  if (!group) return null;
  return getGroupBBoxCenter(group.id);
});

const selectedGroupFirstBlockId = computed(() => {
  const blockId = selectedBlockIds.value[0];
  if (blockId == null) return null;
  const group = getGroupContainingBlock(blockId);
  if (!group) return null;
  const ids = getGroupBlockIds(group.id);
  return ids[0] ?? null;
});

function getSVGPoint(clientX: number, clientY: number): { x: number; y: number } {
  if (!svgRef.value) return { x: 0, y: 0 };
  const pt = svgRef.value.createSVGPoint();
  pt.x = clientX;
  pt.y = clientY;
  const ctm = svgRef.value.getScreenCTM();
  if (!ctm) return { x: 0, y: 0 };
  const p = pt.matrixTransform(ctm.inverse());
  return { x: p.x, y: p.y };
}

const isGroupRotating = ref(false);
const groupRotateStart = ref({ angleDeg: 0, blockRotation: 0, blockId: 0 });

const handleGroupRotateMove = (event: MouseEvent) => {
  if (!isGroupRotating.value || !selectedGroupCenter.value) return;
  const p = getSVGPoint(event.clientX, event.clientY);
  const angleDeg = (Math.atan2(p.y - selectedGroupCenter.value.y, p.x - selectedGroupCenter.value.x) * 180) / Math.PI;
  const delta = angleDeg - groupRotateStart.value.angleDeg;
  let newRotation = groupRotateStart.value.blockRotation + delta;
  if (event.shiftKey) newRotation = Math.round(newRotation / 45) * 45;
  newRotation = normalizeAngleDeg(newRotation);
  handleUpdateBlock(groupRotateStart.value.blockId, { rotation: newRotation }, false);
};

const handleGroupRotateEnd = () => {
  if (!isGroupRotating.value) return;
  isGroupRotating.value = false;
  window.removeEventListener('mousemove', handleGroupRotateMove as EventListener);
  window.removeEventListener('mouseup', handleGroupRotateEnd as EventListener);
  saveCanvasState(blocks.value, true);
};

const handleGroupRotateStart = (event: MouseEvent) => {
  const bid = selectedGroupFirstBlockId.value;
  const center = selectedGroupCenter.value;
  if (bid == null || !center) return;
  const block = blocks.value.find((b) => b.id === bid);
  if (!block) return;
  hypothesesStore.pushUndoSnapshot();
  const p = getSVGPoint(event.clientX, event.clientY);
  const angleDeg = (Math.atan2(p.y - center.y, p.x - center.x) * 180) / Math.PI;
  isGroupRotating.value = true;
  groupRotateStart.value = { angleDeg, blockRotation: normalizeAngleDeg(Number(block.rotation) || 0), blockId: bid };
  window.addEventListener('mousemove', handleGroupRotateMove as EventListener);
  window.addEventListener('mouseup', handleGroupRotateEnd as EventListener);
  event.preventDefault();
  event.stopPropagation();
};

const handleCreateGroup = () => {
  if (!currentHypothesis.value || selectedBlockIds.value.length < 2) return;
  hypothesesStore.pushUndoSnapshot();
  const newGroup: CanvasGroup = {
    id: crypto.randomUUID(),
    parentId: null,
    blockIds: [...selectedBlockIds.value],
    groupIds: [],
  };
  const newGroups = [...(currentHypothesis.value.groups ?? []), newGroup];
  hypothesesStore.setGroupsLocally(newGroups);
  saveCanvasState(blocks.value, true);
};

const handleUngroupSelection = () => {
  if (!currentHypothesis.value) return;
  hypothesesStore.pushUndoSnapshot();
  const toRemove = new Set(selectedBlockIds.value);
  const newGroups = (currentHypothesis.value.groups ?? [])
    .map((g) => ({ ...g, blockIds: g.blockIds.filter((id) => !toRemove.has(id)) }))
    .filter((g) => g.blockIds.length > 0 || g.groupIds.length > 0);
  hypothesesStore.setGroupsLocally(newGroups);
  saveCanvasState(blocks.value, true);
};

const handleSelectBlock = (blockId: number, event?: MouseEvent) => {
  let effectiveBlockId = blockId;
  if (event?.altKey) {
    const svgP = getSVGPoint(event.clientX, event.clientY);
    const stack = hitTestBlocksStack(svgP.x, svgP.y);
    if (stack.length > 1) {
      const clickedIdx = stack.indexOf(blockId);
      if (clickedIdx >= 0) {
        effectiveBlockId = stack[(clickedIdx + 1) % stack.length];
      } else {
        effectiveBlockId = stack[1];
      }
    }
  }

  const multi = event?.ctrlKey || event?.metaKey;
  if (multi) {
    const idx = selectedBlockIds.value.indexOf(effectiveBlockId);
    if (idx >= 0) {
      selectedBlockIds.value = selectedBlockIds.value.filter((id) => id !== effectiveBlockId);
    } else {
      selectedBlockIds.value = [...selectedBlockIds.value, effectiveBlockId];
    }
  } else {
    selectedBlockIds.value = [effectiveBlockId];
  }
  selectedAnnotationId.value = null;

  const block = blocks.value.find((b) => b.id === effectiveBlockId);
  if (block && block.bloc) {
    const bloc = blocsStore.blocs.find((b) => b.id === block.bloc.id);
    if (bloc) {
      blocsStore.setSelectedBloc(bloc);
      window.dispatchEvent(new CustomEvent('highlight-bloc-in-library', { detail: { blocId: bloc.id } }));
    }
  }

  // Améliore la sélection en zones superposées: l'objet actif passe devant.
  const topZ = blocks.value.reduce((max, b) => Math.max(max, Number(b.z_order) || 0), 0);
  if (block && Number(block.z_order) < topZ) {
    hypothesesStore.updateBlockLocally(effectiveBlockId, { z_order: topZ + 1 });
    saveCanvasState(blocks.value);
  }
};

const handleUpdateBlock = async (blockId: number, updates: any, save: boolean = true) => {
  if (!currentHypothesis.value) return;

  const block = blocks.value.find((b) => b.id === blockId);
  if (!block) return;

  if (save) hypothesesStore.pushUndoSnapshot();

  const hasPosition = updates.position_x !== undefined || updates.position_y !== undefined;
  const hasRotation = updates.rotation !== undefined;
  const hasLocked = updates.locked !== undefined;

  // Verrouillage : propager à tout le groupe
  if (hasLocked) {
    const lockGroup = getGroupContainingBlock(blockId);
    const lockIds = lockGroup != null ? getGroupBlockIds(lockGroup.id) : [blockId];
    const lockUpdates = lockIds.map((id) => ({ blockId: id, updates: { locked: updates.locked } }));
    hypothesesStore.updateBlocksLocally(lockUpdates);
    if (save) saveCanvasState(blocks.value);
    return;
  }

  const group = (hasPosition || hasRotation) ? getGroupContainingBlock(blockId) : null;
  const blockIdsToUpdate = group != null ? getGroupBlockIds(group.id) : [blockId];

  if (blockIdsToUpdate.length === 1) {
    const u = { ...updates };
    if (u.rotation !== undefined) u.rotation = normalizeAngleDeg(u.rotation);
    hypothesesStore.updateBlockLocally(blockId, u);
  } else if (hasPosition && blockIdsToUpdate.length > 1) {
    const oldX = Number(block.position_x);
    const oldY = Number(block.position_y);
    const newX = updates.position_x !== undefined ? updates.position_x : oldX;
    const newY = updates.position_y !== undefined ? updates.position_y : oldY;
    const deltaX = newX - oldX;
    const deltaY = newY - oldY;
    const updatesList = blockIdsToUpdate.map((id) => {
      const b = blocks.value.find((x) => x.id === id);
      return {
        blockId: id,
        updates: {
          position_x: Number(b?.position_x ?? 0) + deltaX,
          position_y: Number(b?.position_y ?? 0) + deltaY,
        },
      };
    });
    hypothesesStore.updateBlocksLocally(updatesList);
  } else if (hasRotation && blockIdsToUpdate.length > 1) {
    const deltaAngleDeg = normalizeAngleDeg(Number(updates.rotation)) - normalizeAngleDeg(Number(block.rotation));
    const groupCenter = group ? getGroupBBoxCenter(group.id) : null;
    if (!groupCenter) {
      hypothesesStore.updateBlockLocally(blockId, updates);
    } else {
      const groupCenterX = groupCenter.x;
      const groupCenterY = groupCenter.y;
      const blockData = blockIdsToUpdate.map((id) => {
        const b = blocks.value.find((x) => x.id === id)!;
        const { width: w, height: h } = getBlockCanvasDimensions(b);
        return { block: b, w, h };
      });
      const updatesList = blockData.map(({ block, w, h }) => {
        const cx = Number(block.position_x) + w / 2;
        const cy = Number(block.position_y) + h / 2;
        const newCenter = rotatePoint(cx, cy, groupCenterX, groupCenterY, deltaAngleDeg);
        const newRotation = normalizeAngleDeg(normalizeAngleDeg(Number(block.rotation) || 0) + deltaAngleDeg);
        return {
          blockId: block.id,
          updates: {
            position_x: newCenter.x - w / 2,
            position_y: newCenter.y - h / 2,
            rotation: newRotation,
          },
        };
      });
      hypothesesStore.updateBlocksLocally(updatesList);
    }
  } else {
    hypothesesStore.updateBlockLocally(blockId, updates);
  }

  if (save) {
    saveCanvasState(blocks.value);
  }
};

const doSaveNow = async (blocksToSave: any[]) => {
  if (!currentHypothesis.value) return;

  const selectedBlocIds = selectedBlockIds.value
    .map((id) => blocksToSave.find((b) => b.id === id)?.bloc_id ?? blocksToSave.find((b) => b.id === id)?.bloc?.id)
    .filter((id): id is number => id != null);

  const state = {
    blocks: blocksToSave.map((b) => ({
      bloc_id: b.bloc_id || b.bloc?.id,
      position_x: b.position_x,
      position_y: b.position_y,
      rotation: b.rotation || 0,
      z_order: b.z_order || 0,
      locked: Boolean((b as any).locked),
    })),
    groups: currentHypothesis.value.groups ?? [],
  };

  try {
    if (canvasSaveRetryTimeoutId !== null) {
      clearTimeout(canvasSaveRetryTimeoutId);
      canvasSaveRetryTimeoutId = null;
    }
    window.dispatchEvent(new CustomEvent('save-status', { detail: { status: 'saving' } }));
    await hypothesesStore.saveCanvasState(currentHypothesis.value.id, state);
    window.dispatchEvent(new CustomEvent('save-status', { detail: { status: 'saved' } }));
    canvasSaveRetryDelayMs = 1500;
    if (selectedBlocIds.length > 0 && hypothesesStore.currentHypothesis?.blocks) {
      selectedBlockIds.value = selectedBlocIds.flatMap((blocId) => {
        const b = hypothesesStore.currentHypothesis!.blocks.find(
          (x: any) => (x.bloc_id ?? x.bloc?.id) === blocId
        );
        return b ? [b.id] : [];
      });
    }
  } catch (error) {
    pendingBlocksToSave = blocksToSave;
    if (canvasSaveRetryTimeoutId === null) {
      canvasSaveRetryTimeoutId = setTimeout(() => {
        canvasSaveRetryTimeoutId = null;
        if (pendingBlocksToSave != null) {
          const retryPayload = pendingBlocksToSave;
          pendingBlocksToSave = null;
          lastSaveTime = Date.now();
          void doSaveNow(retryPayload);
        }
      }, canvasSaveRetryDelayMs);
      canvasSaveRetryDelayMs = Math.min(canvasSaveRetryDelayMs * 2, CANVAS_SAVE_RETRY_MAX_MS);
    }
    if (import.meta.env.DEV) {
      console.error('[CanvasViewer] Error saving canvas state:', error);
    }
  }
};

const saveCanvasState = (updatedBlocks: any[], forceImmediate = false) => {
  if (!currentHypothesis.value) return;

  const now = Date.now();
  const elapsed = now - lastSaveTime;

  if (forceImmediate || elapsed >= SAVE_THROTTLE_MS || lastSaveTime === 0) {
    if (saveTimeoutId !== null) {
      clearTimeout(saveTimeoutId);
      saveTimeoutId = null;
    }
    pendingBlocksToSave = null;
    lastSaveTime = now;
    doSaveNow(updatedBlocks);
    return;
  }

  pendingBlocksToSave = updatedBlocks;
  if (saveTimeoutId === null) {
    saveTimeoutId = setTimeout(() => {
      const toSave = pendingBlocksToSave ?? blocks.value;
      pendingBlocksToSave = null;
      saveTimeoutId = null;
      lastSaveTime = Date.now();
      doSaveNow(toSave);
    }, SAVE_THROTTLE_MS - elapsed);
  }
};

const handleMouseDown = (event: MouseEvent) => {
  // Ignorer les clics sur les contrôles
  const target = event.target as HTMLElement;
  if (target.closest('.canvas-controls')) {
    return;
  }

  const svgTarget = event.target as SVGElement;

  // Ignorer les clics sur les annotations (gérés par les composants d'annotation)
  if (svgTarget.closest('.label-annotation') || 
      svgTarget.closest('.line-annotation') ||
      svgTarget.closest('.rectangle-annotation') ||
      svgTarget.closest('.circle-annotation') ||
      svgTarget.closest('.measure-annotation')) {
    return;
  }

  if (event.button !== 0) return;

  // L'event arrive ici si : clic sur le fond SVG, OU zone transparente d'un bloc (pas de stopPropagation)
  const isFromBlock = Boolean(svgTarget.closest('.block-instance'));
  const fillAttr = svgTarget.getAttribute('fill');
  const isFondSvg = svgTarget.tagName === 'rect' &&
    (svgTarget.hasAttribute('data-canvas-base') ||
      fillAttr === 'transparent' ||
      fillAttr === 'url(#grid)' ||
      Boolean(fillAttr?.startsWith('url(#')));

  if (!isFromBlock && !isFondSvg) return;

  // Si l'event vient d'un bloc (zone transparente), chercher un bloc opaque en dessous
  if (isFromBlock) {
    const svgP = getSVGPoint(event.clientX, event.clientY);
    // Trouver le bloc source (celui dont la zone transparente a été cliquée)
    const sourceBlockEl = (svgTarget.closest('.block-instance') as SVGElement | null);
    // On cherche un bloc opaque sous le curseur (tous les blocs, le hit-test gère le z-order)
    const hitBlockId = hitTestBlocks(svgP.x, svgP.y);
    if (hitBlockId !== null) {
      // Sélectionner ce bloc comme si BlockInstance avait émis 'select'
      handleSelectBlock(hitBlockId, event);
      event.preventDefault();
      return;
    }
    // Aucun bloc opaque trouvé → traiter comme fond (pan)
    void sourceBlockEl; // unused
  }

  // Fond SVG ou zone transparente sans bloc en dessous : annotations ou pan
  if (activeAnnotationTool.value === 'label') { handleCreateLabel(event); return; }
  if (activeAnnotationTool.value === 'line') { handleCreateLine(event); return; }
  if (activeAnnotationTool.value === 'rectangle') { handleCreateRectangle(event); return; }
  if (activeAnnotationTool.value === 'circle') { handleCreateCircle(event); return; }
  if (activeAnnotationTool.value === 'measure') { handleCreateMeasure(event); return; }

  selectedBlockIds.value = [];
  selectedAnnotationId.value = null;
  isPanning.value = true;
  panStart.value = { x: event.clientX, y: event.clientY };
  event.preventDefault();
};

const handleMouseUp = (event: MouseEvent) => {
  if (isPanning.value && event.button === 0) {
    isPanning.value = false;
  }
};

const handleMouseMove = (event: MouseEvent) => {
  if (!isPanning.value || !svgRef.value) return;
  const rect = svgRef.value.getBoundingClientRect();
  const k = Math.min(rect.width / CANVAS_WORLD_WIDTH, rect.height / CANVAS_WORLD_HEIGHT);
  if (k <= 0 || !Number.isFinite(k)) return;
  const deltaX = event.clientX - panStart.value.x;
  const deltaY = event.clientY - panStart.value.y;
  panOffset.value = {
    x: panOffset.value.x + deltaX / k,
    y: panOffset.value.y + deltaY / k,
  };
  panStart.value = { x: event.clientX, y: event.clientY };
  event.preventDefault();
};

const handleWheel = (event: WheelEvent) => {
  event.preventDefault();
  if (!svgRef.value) return;
  const rect = svgRef.value.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return;
  const k = Math.min(rect.width / CANVAS_WORLD_WIDTH, rect.height / CANVAS_WORLD_HEIGHT);
  if (k <= 0 || !Number.isFinite(k)) return;

  const pt = svgRef.value.createSVGPoint();
  pt.x = event.clientX;
  pt.y = event.clientY;
  const ctm = svgRef.value.getScreenCTM();
  if (!ctm) return;
  const svgP = pt.matrixTransform(ctm.inverse());
  if (!Number.isFinite(svgP.x) || !Number.isFinite(svgP.y)) return;

  // Sensibilité : deltaY ~100 par cran (un peu plus réactif qu'avant)
  const sensitivity = 0.0005;
  const scale = Math.max(0.5, Math.min(1.5, 1 - event.deltaY * sensitivity));
  const newZoom = clampZoom(zoomLevel.value * scale);

  // preserveAspectRatio="xMidYMid meet" centre le viewBox : offset letterbox
  const offsetX = (rect.width - CANVAS_WORLD_WIDTH * k) / 2;
  const offsetY = (rect.height - CANVAS_WORLD_HEIGHT * k) / 2;
  panOffset.value = {
    x: (event.clientX - rect.left - offsetX) / k - svgP.x * newZoom,
    y: (event.clientY - rect.top - offsetY) / k - svgP.y * newZoom,
  };
  zoomLevel.value = newZoom;
};

const handleContextMenu = (event: MouseEvent) => {
  // Empêcher le menu contextuel par défaut
  event.preventDefault();
};

// Support tactile pour mobile/tablette
const handleTouchStart = (event: TouchEvent) => {
  if (event.touches.length === 1) {
    const touch = event.touches[0];
    touchStartPos.value = { x: touch.clientX, y: touch.clientY };
    
    // Vérifier si on touche un bloc
    const target = event.target as SVGElement;
    if (!target.closest('.block-instance') && !target.closest('.block-clickable')) {
      // Toucher le fond → pan
      isPanning.value = true;
      panStart.value = { x: touch.clientX, y: touch.clientY };
    }
  }
};

const handleTouchMove = (event: TouchEvent) => {
  if (event.touches.length !== 1 || !isPanning.value || !svgRef.value) return;
  const rect = svgRef.value.getBoundingClientRect();
  const k = Math.min(rect.width / CANVAS_WORLD_WIDTH, rect.height / CANVAS_WORLD_HEIGHT);
  if (k <= 0 || !Number.isFinite(k)) return;
  const touch = event.touches[0];
  const deltaX = touch.clientX - panStart.value.x;
  const deltaY = touch.clientY - panStart.value.y;
  panOffset.value = {
    x: panOffset.value.x + deltaX / k,
    y: panOffset.value.y + deltaY / k,
  };
  panStart.value = { x: touch.clientX, y: touch.clientY };
  event.preventDefault();
};

const handleTouchEnd = () => {
  isPanning.value = false;
  touchStartPos.value = null;
};

const zoomIn = () => {
  zoomLevel.value = clampZoom(zoomLevel.value * 1.2);
};

const zoomOut = () => {
  zoomLevel.value = clampZoom(zoomLevel.value / 1.2);
};

const resetZoom = () => {
  zoomLevel.value = 1;
  panOffset.value = { x: 0, y: 0 };
  if (containerRef.value) {
    containerRef.value.scrollTo({ left: 0, top: 0, behavior: 'smooth' });
  }
};

/** Union blocs (AABB avec rotation) + annotations visibles pour ajuster la vue. */
function getCanvasContentBoundsForFit(): { minX: number; minY: number; maxX: number; maxY: number } | null {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  const grow = (ax: number, ay: number, bx: number, by: number) => {
    minX = Math.min(minX, ax, bx);
    maxX = Math.max(maxX, ax, bx);
    minY = Math.min(minY, ay, by);
    maxY = Math.max(maxY, ay, by);
  };

  for (const block of blocks.value) {
    const box = getHypothesisBlockRotatedAabb(block);
    grow(box.minX, box.minY, box.maxX, box.maxY);
  }

  const LABEL_FS = 120;
  const LABEL_PAD = 60;
  const LABEL_BTN = 240;
  const LABEL_MAX_TW = 2000;

  for (const a of allAnnotations.value) {
    if (!isAnnotationVisible(a)) continue;
    const d = a.data ?? {};
    const sw = (Number(a.stroke_width) || ANNOTATION_DEFAULT_STROKE_PX) / 2;

    if (a.type === 'label') {
      const x = Number(d.x);
      const y = Number(d.y);
      if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
      const text = String(d.text ?? 'Étiquette');
      const tw = Math.min(text.length * LABEL_FS * 0.6, LABEL_MAX_TW);
      const halfH = (LABEL_FS + LABEL_PAD * 2) / 2;
      grow(x - LABEL_PAD, y - halfH, x + tw + LABEL_PAD + LABEL_BTN, y + halfH);
    } else if (a.type === 'line') {
      const x1 = Number(d.x1);
      const y1 = Number(d.y1);
      const x2 = Number(d.x2);
      const y2 = Number(d.y2);
      if (![x1, y1, x2, y2].every(Number.isFinite)) continue;
      const pad = sw + 100;
      grow(x1 - pad, y1 - pad, x2 + pad, y2 + pad);
    } else if (a.type === 'measure') {
      const x1 = Number(d.x1);
      const y1 = Number(d.y1);
      const x2 = Number(d.x2);
      const y2 = Number(d.y2);
      if (![x1, y1, x2, y2].every(Number.isFinite)) continue;
      const pad = sw + 120;
      grow(x1 - pad, y1 - pad, x2 + pad, y2 + pad);
      const midX = (x1 + x2) / 2;
      const midY = (y1 + y2) / 2;
      const dist = Math.hypot(x2 - x1, y2 - y1);
      const lw = Math.min(Math.max(500, dist * 0.4), 1400);
      const lh = 220;
      grow(midX - lw / 2, midY - lh / 2, midX + lw / 2, midY + lh / 2);
    } else if (a.type === 'rectangle') {
      const x1 = Number(d.x1);
      const y1 = Number(d.y1);
      const x2 = Number(d.x2);
      const y2 = Number(d.y2);
      if (![x1, y1, x2, y2].every(Number.isFinite)) continue;
      const pad = sw + 80;
      grow(Math.min(x1, x2) - pad, Math.min(y1, y2) - pad, Math.max(x1, x2) + pad, Math.max(y1, y2) + pad);
    } else if (a.type === 'circle') {
      const cx = Number(d.cx);
      const cy = Number(d.cy);
      const r = Number(d.radius);
      if (![cx, cy, r].every(Number.isFinite)) continue;
      const pad = sw + 100;
      grow(cx - r - pad, cy - r - pad, cx + r + pad, cy + r + pad);
    }
  }

  if (minX === Infinity) return null;

  const MIN_SPAN = 120;
  if (maxX - minX < MIN_SPAN) {
    const c = (minX + maxX) / 2;
    minX = c - MIN_SPAN / 2;
    maxX = c + MIN_SPAN / 2;
  }
  if (maxY - minY < MIN_SPAN) {
    const c = (minY + maxY) / 2;
    minY = c - MIN_SPAN / 2;
    maxY = c + MIN_SPAN / 2;
  }

  return { minX, minY, maxX, maxY };
}

const frameAll = () => {
  if (!containerRef.value || !svgRef.value) {
    resetZoom();
    return;
  }
  const rect = svgRef.value.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) {
    resetZoom();
    return;
  }
  const k = Math.min(rect.width / CANVAS_WORLD_WIDTH, rect.height / CANVAS_WORLD_HEIGHT);
  if (k <= 0 || !Number.isFinite(k)) {
    resetZoom();
    return;
  }

  const bounds = getCanvasContentBoundsForFit();
  if (!bounds) {
    resetZoom();
    return;
  }

  const { minX, minY, maxX, maxY } = bounds;
  const centerX = (minX + maxX) / 2;
  const centerY = (minY + maxY) / 2;
  const bboxWidth = Math.max(maxX - minX, 1);
  const bboxHeight = Math.max(maxY - minY, 1);

  const margin = 0.92;
  const zoomX = (CANVAS_WORLD_WIDTH * margin) / bboxWidth;
  const zoomY = (CANVAS_WORLD_HEIGHT * margin) / bboxHeight;
  const newZoom = clampZoom(Math.min(zoomX, zoomY, ZOOM_MAX));
  if (!Number.isFinite(newZoom)) {
    resetZoom();
    return;
  }

  const startZoom = clampZoom(zoomLevel.value);
  const duration = 400;
  const startTime = performance.now();

  const animate = (currentTime: number) => {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easeProgress = 1 - Math.pow(1 - progress, 3);
    const z = clampZoom(startZoom + (newZoom - startZoom) * easeProgress);
    zoomLevel.value = z;
    panOffset.value = {
      x: CANVAS_WORLD_WIDTH / 2 - centerX * z,
      y: CANVAS_WORLD_HEIGHT / 2 - centerY * z,
    };

    if (progress < 1) {
      requestAnimationFrame(animate);
    }
  };

  requestAnimationFrame(animate);
};

const handleZOrderUp = (blockId: number) => {
  const block = blocks.value.find((b) => b.id === blockId);
  if (!block || !currentHypothesis.value) return;

  // Trouver le bloc avec le z_order le plus élevé qui est inférieur
  const blocksWithLowerZ = blocks.value
    .filter((b) => b.z_order > block.z_order)
    .sort((a, b) => a.z_order - b.z_order);

  if (blocksWithLowerZ.length > 0) {
    const targetZ = blocksWithLowerZ[0].z_order;
    hypothesesStore.pushUndoSnapshot();
    const updatedBlocks = blocks.value.map((b) =>
      b.id === blockId ? { ...b, z_order: targetZ } : b
    );
    saveCanvasState(updatedBlocks);
  }
};

const handleZOrderDown = (blockId: number) => {
  const block = blocks.value.find((b) => b.id === blockId);
  if (!block || !currentHypothesis.value) return;

  const blocksWithHigherZ = blocks.value
    .filter((b) => b.z_order < block.z_order)
    .sort((a, b) => b.z_order - a.z_order);

  if (blocksWithHigherZ.length > 0) {
    const targetZ = blocksWithHigherZ[0].z_order;
    hypothesesStore.pushUndoSnapshot();
    const updatedBlocks = blocks.value.map((b) =>
      b.id === blockId ? { ...b, z_order: targetZ } : b
    );
    saveCanvasState(updatedBlocks);
  }
};

const handleDeleteBlock = (blockId: number) => {
  const inSelection = selectedBlockIds.value.includes(blockId) && selectedBlockIds.value.length > 1;
  blocksToDelete.value = inSelection ? [...selectedBlockIds.value] : [blockId];
  showDeleteConfirm.value = true;
};

// Gestion des annotations
const handleCreateLabel = async (event: MouseEvent) => {
  console.log('[CanvasViewer] Création étiquette...');
  if (!svgRef.value || !currentHypothesis.value) {
    console.error('[CanvasViewer] svgRef ou currentHypothesis manquant');
    return;
  }

  const svg = svgRef.value;
  const pt = svg.createSVGPoint();
  pt.x = event.clientX;
  pt.y = event.clientY;
  const svgP = pt.matrixTransform(svg.getScreenCTM()?.inverse());
  
  console.log('[CanvasViewer] Position SVG:', svgP);

  // Créer une annotation via l'API
  try {
    console.log('[CanvasViewer] Appel API create...');
    const newAnnotation = await annotationsApi.create({
      hypothesis_id: currentHypothesis.value.id,
      type: 'label',
      data: {
        x: svgP.x,
        y: svgP.y,
        text: '',
      },
      stroke_width: ANNOTATION_DEFAULT_STROKE_PX,
    });
    console.log('[CanvasViewer] Annotation créée:', newAnnotation);

    // Recharger l'hypothèse pour avoir les annotations à jour
    await hypothesesStore.loadHypothesis(currentHypothesis.value.id);
    selectedAnnotationId.value = newAnnotation.id;
    
    // Désélectionner l'outil après création
    activeAnnotationTool.value = null;
    window.dispatchEvent(
      new CustomEvent('annotation-tool-selected', {
        detail: { tool: null },
      })
    );
    window.dispatchEvent(new CustomEvent('annotation-tool-selected', { detail: { tool: null } }));
    console.log('[CanvasViewer] Outil désélectionné');
  } catch (error) {
    console.error('[CanvasViewer] Erreur création annotation:', error);
  }
};

const handleCreateLine = async (event: MouseEvent) => {
  console.log('[CanvasViewer] Création ligne...');
  if (!svgRef.value || !currentHypothesis.value) {
    console.error('[CanvasViewer] svgRef ou currentHypothesis manquant');
    return;
  }

  const svg = svgRef.value;
  const pt = svg.createSVGPoint();
  pt.x = event.clientX;
  pt.y = event.clientY;
  const svgP = pt.matrixTransform(svg.getScreenCTM()?.inverse());
  
  console.log('[CanvasViewer] Position SVG:', svgP);

  // Créer une ligne avec un point de départ et d'arrivée
  try {
    console.log('[CanvasViewer] Appel API create...');
    const lineLen = annotationMmToCanvasPx(ANNOTATION_DEFAULT_LINE_LENGTH_MM);
    const newAnnotation = await annotationsApi.create({
      hypothesis_id: currentHypothesis.value.id,
      type: 'line',
      data: {
        x1: svgP.x,
        y1: svgP.y,
        x2: svgP.x + lineLen,
        y2: svgP.y,
      },
      stroke_width: ANNOTATION_DEFAULT_STROKE_PX,
    });
    console.log('[CanvasViewer] Ligne créée:', newAnnotation);

    // Recharger l'hypothèse pour avoir les annotations à jour
    await hypothesesStore.loadHypothesis(currentHypothesis.value.id);
    selectedAnnotationId.value = newAnnotation.id;
    
    // Désélectionner l'outil après création
    activeAnnotationTool.value = null;
    window.dispatchEvent(new CustomEvent('annotation-tool-selected', { detail: { tool: null } }));
  } catch (error) {
    console.error('[CanvasViewer] Erreur création ligne:', error);
  }
};

const handleCreateRectangle = async (event: MouseEvent) => {
  console.log('[CanvasViewer] Création rectangle...');
  if (!svgRef.value || !currentHypothesis.value) {
    console.error('[CanvasViewer] svgRef ou currentHypothesis manquant');
    return;
  }

  const svg = svgRef.value;
  const pt = svg.createSVGPoint();
  pt.x = event.clientX;
  pt.y = event.clientY;
  const svgP = pt.matrixTransform(svg.getScreenCTM()?.inverse());
  
  try {
    const rw = annotationMmToCanvasPx(ANNOTATION_DEFAULT_RECT_WIDTH_MM);
    const rh = annotationMmToCanvasPx(ANNOTATION_DEFAULT_RECT_HEIGHT_MM);
    const newAnnotation = await annotationsApi.create({
      hypothesis_id: currentHypothesis.value.id,
      type: 'rectangle',
      data: {
        x1: svgP.x,
        y1: svgP.y,
        x2: svgP.x + rw,
        y2: svgP.y + rh,
      },
      stroke_width: ANNOTATION_DEFAULT_STROKE_PX,
    });
    console.log('[CanvasViewer] Rectangle créé:', newAnnotation);

    await hypothesesStore.loadHypothesis(currentHypothesis.value.id);
    selectedAnnotationId.value = newAnnotation.id;
    
    activeAnnotationTool.value = null;
    window.dispatchEvent(new CustomEvent('annotation-tool-selected', { detail: { tool: null } }));
  } catch (error) {
    console.error('[CanvasViewer] Erreur création rectangle:', error);
  }
};

const handleCreateCircle = async (event: MouseEvent) => {
  console.log('[CanvasViewer] Création cercle...');
  if (!svgRef.value || !currentHypothesis.value) {
    console.error('[CanvasViewer] svgRef ou currentHypothesis manquant');
    return;
  }

  const svg = svgRef.value;
  const pt = svg.createSVGPoint();
  pt.x = event.clientX;
  pt.y = event.clientY;
  const svgP = pt.matrixTransform(svg.getScreenCTM()?.inverse());
  
  try {
    const r = annotationMmToCanvasPx(ANNOTATION_DEFAULT_CIRCLE_DIAMETER_MM / 2);
    const newAnnotation = await annotationsApi.create({
      hypothesis_id: currentHypothesis.value.id,
      type: 'circle',
      data: {
        cx: svgP.x,
        cy: svgP.y,
        radius: r,
      },
      stroke_width: ANNOTATION_DEFAULT_STROKE_PX,
    });
    console.log('[CanvasViewer] Cercle créé:', newAnnotation);

    await hypothesesStore.loadHypothesis(currentHypothesis.value.id);
    selectedAnnotationId.value = newAnnotation.id;
    
    activeAnnotationTool.value = null;
    window.dispatchEvent(new CustomEvent('annotation-tool-selected', { detail: { tool: null } }));
  } catch (error) {
    console.error('[CanvasViewer] Erreur création cercle:', error);
  }
};

const handleCreateMeasure = async (event: MouseEvent) => {
  console.log('[CanvasViewer] Création mesure...');
  if (!svgRef.value || !currentHypothesis.value) {
    console.error('[CanvasViewer] svgRef ou currentHypothesis manquant');
    return;
  }

  const svg = svgRef.value;
  const pt = svg.createSVGPoint();
  pt.x = event.clientX;
  pt.y = event.clientY;
  const svgP = pt.matrixTransform(svg.getScreenCTM()?.inverse());
  
  try {
    const measLen = annotationMmToCanvasPx(ANNOTATION_DEFAULT_LINE_LENGTH_MM);
    const newAnnotation = await annotationsApi.create({
      hypothesis_id: currentHypothesis.value.id,
      type: 'measure',
      data: {
        x1: svgP.x,
        y1: svgP.y,
        x2: svgP.x + measLen,
        y2: svgP.y,
      },
      stroke_width: ANNOTATION_DEFAULT_STROKE_PX,
    });
    console.log('[CanvasViewer] Mesure créée:', newAnnotation);

    await hypothesesStore.loadHypothesis(currentHypothesis.value.id);
    selectedAnnotationId.value = newAnnotation.id;
    
    activeAnnotationTool.value = null;
    window.dispatchEvent(new CustomEvent('annotation-tool-selected', { detail: { tool: null } }));
  } catch (error) {
    console.error('[CanvasViewer] Erreur création mesure:', error);
  }
};

const handleSelectAnnotation = (annotation: Annotation) => {
  if (selectedAnnotationId.value === annotation.id) return;
  selectedAnnotationId.value = annotation.id;
  selectedBlockIds.value = [];
  window.dispatchEvent(
    new CustomEvent('annotation-selected', {
      detail: { annotation },
    })
  );
};

const handleUpdateAnnotation = (annotation: Annotation, data: any, save = true) => {
  if (!currentHypothesis.value) return;
  
  // Mise à jour locale immédiate pour la réactivité
  const annIndex = currentHypothesis.value.annotations.findIndex(a => a.id === annotation.id);
  if (annIndex !== -1) {
    currentHypothesis.value.annotations[annIndex] = {
      ...annotation,
      data,
    };
  }

  if (!save) return;
  pendingAnnotationSaves.set(annotation.id, { annotation, data });
  scheduleAnnotationSave(save);
};

const handleMoveAnnotation = (annotation: Annotation, x: number, y: number) => {
  handleUpdateAnnotation(
    annotation,
    {
      ...annotation.data,
      x,
      y,
    },
    true
  );
};

const handleDeleteAnnotation = async (annotation: Annotation) => {
  if (!currentHypothesis.value) return;
  
  try {
    pendingAnnotationSaves.delete(annotation.id);
    await annotationsApi.delete(annotation.id);
    
    selectedAnnotationId.value = null;
    // Recharger l'hypothèse pour avoir les annotations à jour
    await hypothesesStore.loadHypothesis(currentHypothesis.value.id);
  } catch (error) {
    console.error('Erreur suppression annotation:', error);
  }
};

const confirmDelete = async () => {
  if (blocksToDelete.value.length === 0 || !currentHypothesis.value) return;
  const toDeleteSet = new Set(blocksToDelete.value);

  hypothesesStore.pushUndoSnapshot();
  const updatedBlocks = blocks.value.filter((b) => !toDeleteSet.has(b.id));
  const groups = currentHypothesis.value.groups ?? [];
  const cleanedGroups = groups
    .map((g) => ({ ...g, blockIds: g.blockIds.filter((id) => !toDeleteSet.has(id)) }))
    .filter((g) => g.blockIds.length > 0 || g.groupIds.length > 0);
  hypothesesStore.setGroupsLocally(cleanedGroups);
  saveCanvasState(updatedBlocks, true);
  selectedBlockIds.value = [];
  showDeleteConfirm.value = false;
  blocksToDelete.value = [];
};

const cancelDelete = () => {
  showDeleteConfirm.value = false;
  blocksToDelete.value = [];
};

const pendingDropCenter = ref<{ blocId: number; x: number; y: number } | null>(null);

const handleDrop = async (event: DragEvent) => {
  isDraggingFromLibrary.value = false;
  if (!currentHypothesis.value || !svgRef.value) return;

  const blocIdStr = event.dataTransfer?.getData('bloc-id');
  if (!blocIdStr) return;
  const blocId = parseInt(blocIdStr, 10);

  const pt = svgRef.value.createSVGPoint();
  pt.x = event.clientX;
  pt.y = event.clientY;
  const ctm = svgRef.value.getScreenCTM();
  if (!ctm) return;
  const svgP = pt.matrixTransform(ctm.inverse());

  pendingDropCenter.value = { blocId, x: svgP.x, y: svgP.y };
  const nextZOrder = blocks.value.reduce((max, b) => Math.max(max, Number(b.z_order) || 0), 0) + 1;

  hypothesesStore.pushUndoSnapshot();
  const newBlocks = [
    ...blocks.value,
    {
      id: Date.now(),
      hypothesis_id: currentHypothesis.value.id,
      bloc_id: blocId,
      position_x: svgP.x,
      position_y: svgP.y,
      rotation: 0,
      z_order: nextZOrder,
      locked: false,
      created_at: new Date().toISOString(),
      bloc: blocsStore.blocs.find((b) => b.id === blocId),
    },
  ];

  saveCanvasState(newBlocks, true);
};

const updateCanvasSize = () => {
  if (svgRef.value) {
    const r = svgRef.value.getBoundingClientRect();
    canvasRectRef.value = { width: r.width, height: r.height };
  }
};

const canvasPixelsPerUser = computed(() => {
  const { width: w, height: h } = canvasRectRef.value;
  const z = safeZoomLevel.value;
  if (w <= 0 || h <= 0) return 1;
  return Math.min(
    (w * z) / CANVAS_WORLD_WIDTH,
    (h * z) / CANVAS_WORLD_HEIGHT
  );
});

/** Centre haut du cadre groupe, rayon / trait / icône comme un bloc seul (BlockInstance). */
const groupRotateHandleLayout = computed(() => {
  const bid = selectedBlockIds.value[0];
  if (bid == null) return null;
  const group = getGroupContainingBlock(bid);
  if (!group) return null;
  const box = getGroupAxisAlignedBBox(group.id);
  if (!box) return null;
  const ppu = Math.max(canvasPixelsPerUser.value, 0.01);
  return {
    cx: box.x + box.width / 2,
    cy: box.y - GROUP_ROTATE_HANDLE_OFFSET_USER,
    r: GROUP_CONTROL_RADIUS_PX / ppu,
    strokeW: GROUP_CONTROL_STROKE_PX / ppu,
    iconFont: GROUP_CONTROL_ICON_PX / ppu,
  };
});

const frameBlockInCanvas = (blocId: number) => {
  const block = blocks.value.find((b) => b.bloc?.id === blocId || b.bloc_id === blocId);
  if (!block || !svgRef.value) return;

  const { width: bw, height: bh } = getBlockCanvasDimensions(block);
  const halfW = bw / 2;
  const halfH = bh / 2;
  const centerX = block.position_x + halfW;
  const centerY = block.position_y + halfH;
  const z = safeZoomLevel.value;

  const targetPan = {
    x: CANVAS_WORLD_WIDTH / 2 - centerX * z,
    y: CANVAS_WORLD_HEIGHT / 2 - centerY * z,
  };

  // Animation fluide vers la cible
  const startPan = { ...panOffset.value };
  const duration = 400; // ms
  const startTime = performance.now();

  const animate = (currentTime: number) => {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    
    // Easing ease-out pour un mouvement fluide
    const easeProgress = 1 - Math.pow(1 - progress, 3);
    
    panOffset.value = {
      x: startPan.x + (targetPan.x - startPan.x) * easeProgress,
      y: startPan.y + (targetPan.y - startPan.y) * easeProgress,
    };

    if (progress < 1) {
      requestAnimationFrame(animate);
    }
  };

  requestAnimationFrame(animate);
  selectedBlockIds.value = [block.id];
};

const handleBlockDimensions = (event: CustomEvent<{ blocId: number; blockId: number; width: number; height: number }>) => {
  const { blocId, blockId, width, height } = event.detail;
  const map = new Map(blockDimensionsByBlocId.value);
  map.set(blocId, { width, height });
  blockDimensionsByBlocId.value = map;

  if (pendingDropCenter.value?.blocId === blocId) {
    const center = pendingDropCenter.value;
    pendingDropCenter.value = null;
    const block = blocks.value.find((b) => b.id === blockId);
    if (block) {
      handleUpdateBlock(blockId, {
        position_x: center.x - width / 2,
        position_y: center.y - height / 2,
      }, true);
    }
  }
};

const handleFrameBlock = (event: CustomEvent) => {
  const { blocId } = event.detail;
  frameBlockInCanvas(blocId);
};

const handleKeyDown = async (event: KeyboardEvent) => {
  const ctrlOrMeta = event.ctrlKey || event.metaKey;
  if (ctrlOrMeta && event.key.toLowerCase() === 'z') {
    event.preventDefault();
    try {
      if (event.shiftKey) {
        await hypothesesStore.redo();
      } else {
        await hypothesesStore.undo();
      }
      window.dispatchEvent(new CustomEvent('save-status', { detail: { status: 'saved' } }));
    } catch {
      window.dispatchEvent(new CustomEvent('save-status', { detail: { status: 'error' } }));
    }
    return;
  }
  if (ctrlOrMeta && event.key.toLowerCase() === 's') {
    event.preventDefault();
    if (currentHypothesis.value && blocks.value.length > 0) {
      saveCanvasState(blocks.value, true);
    }
    return;
  }
  if (ctrlOrMeta && (event.key === '=' || event.key === '+')) {
    event.preventDefault();
    zoomIn();
    return;
  }
  if (ctrlOrMeta && event.key === '-') {
    event.preventDefault();
    zoomOut();
    return;
  }
  if (ctrlOrMeta && event.key === '0') {
    event.preventDefault();
    frameAll();
    return;
  }
  if (event.key === 'Escape') {
    selectedBlockIds.value = [];
    selectedAnnotationId.value = null;
  }
};

const handleDragEnterCanvas = () => {
  isDraggingFromLibrary.value = true;
};

const handleDragLeaveCanvas = () => {
  isDraggingFromLibrary.value = false;
};

const handleAnnotationToolSelected = (event: CustomEvent) => {
  activeAnnotationTool.value = event.detail.tool;
  console.log('[CanvasViewer] Outil annotation sélectionné:', activeAnnotationTool.value);
  // Désélectionner les blocs quand un outil d'annotation est actif
  if (activeAnnotationTool.value) {
    selectedBlockIds.value = [];
  }
};

const handleAnnotationSelected = (event: Event) => {
  const customEvent = event as CustomEvent<{ annotation?: Annotation }>;
  const annotationId = customEvent.detail?.annotation?.id;
  if (typeof annotationId !== 'number') return;
  if (selectedAnnotationId.value === annotationId) return;
  selectedAnnotationId.value = annotationId;
  selectedBlockIds.value = [];
};

const handleAnnotationEditRequested = (event: Event) => {
  const customEvent = event as CustomEvent;
  const annotation = customEvent.detail.annotation;
  
  // Sélectionner l'annotation
  selectedAnnotationId.value = annotation.id;
  
  // Déclencher l'édition sur le composant LabelAnnotation
  // On utilise un événement pour communiquer avec le composant enfant
  window.dispatchEvent(
    new CustomEvent('start-annotation-edit', {
      detail: { annotationId: annotation.id },
    })
  );
};

const handleAnnotationsVisibilityChanged = (event: Event) => {
  const customEvent = event as CustomEvent;
  annotationsTemporarilyHidden.value = customEvent.detail.hidden;
};

const EXPORT_MARGIN = 0.05;

const SVG_THEME_EXPORT_VARS: Record<ThemeId, Record<string, string>> = {
  dark: {
    '--cv-canvas-fill': '#1a1a1a',
    '--cv-grid-stroke': '#555555',
    '--cv-accent': '#4a9eff',
    '--cv-annotation-label-default': '#e0e0e0',
    '--cv-label-bg': 'rgba(42, 42, 42, 0.9)',
    '--cv-label-stroke': '#666666',
  },
  light: {
    '--cv-canvas-fill': '#ffffff',
    '--cv-grid-stroke': '#c0c0c0',
    '--cv-accent': '#1565c0',
    '--cv-annotation-label-default': '#1a1a1a',
    '--cv-label-bg': 'rgba(255, 255, 255, 0.92)',
    '--cv-label-stroke': '#5a5a5a',
  },
};

function resolveSvgCssVarsInAttr(attrValue: string, vars: Record<string, string>): string {
  if (!attrValue.includes('var(')) return attrValue;
  return attrValue.replace(/var\((--[a-z0-9-]+)\)/gi, (match, name: string) => vars[name] ?? match);
}

/** Remplace les var() du clone SVG par des couleurs explicites (export PNG hors document). */
function inlineSvgThemeCssVarsForExport(root: SVGSVGElement) {
  const mode: ThemeId = document.body.classList.contains('theme-light') ? 'light' : 'dark';
  const vars = SVG_THEME_EXPORT_VARS[mode];
  root.querySelectorAll('*').forEach((el) => {
    for (const attr of ['fill', 'stroke'] as const) {
      const v = el.getAttribute(attr);
      if (!v || !v.includes('var(')) continue;
      el.setAttribute(attr, resolveSvgCssVarsInAttr(v, vars));
    }
  });
}

/** Retire l’UI éditeur du SVG cloné (poignées, grilles, hitbox) pour un PNG propre. */
function stripExportChromeFromSvg(root: SVGSVGElement) {
  const selectors = [
    '.block-controls',
    '.block-outline',
    '.block-clickable',
    '.group-bbox-layer',
    '.group-rotation-handle',
    '.control-point',
    '.edit-button',
    '.inventory-overlay',
  ];
  for (const sel of selectors) {
    root.querySelectorAll(sel).forEach((el) => el.remove());
  }
  root.querySelectorAll('.block-instance > rect[stroke="lime"]').forEach((el) => el.remove());

  const children = Array.from(root.children);
  const gridRect = children.find(
    (n) =>
      n.tagName === 'rect' &&
      (n as SVGRectElement).getAttribute('fill')?.includes('url(#grid)')
  );
  gridRect?.remove();
}

async function handleExportFitted() {
  if (!currentHypothesis.value || !svgRef.value || blocks.value.length === 0) return;

  const bounds = getCanvasContentBoundsForFit();
  if (!bounds) return;

  const { minX, minY, maxX, maxY } = bounds;
  const bboxW = Math.max(maxX - minX, 1);
  const bboxH = Math.max(maxY - minY, 1);
  const margin = EXPORT_MARGIN;
  const padX = bboxW * margin;
  const padY = bboxH * margin;
  const viewMinX = minX - padX;
  const viewMinY = minY - padY;
  const viewW = bboxW + 2 * padX;
  const viewH = bboxH + 2 * padY;

  const baseW = Math.round(viewW);
  const baseH = Math.round(viewH);
  const mult = Math.max(1, EXPORT_RASTER_MULTIPLIER);
  let outW = Math.round(baseW * mult);
  let outH = Math.round(baseH * mult);
  if (outW > EXPORT_MAX_SIDE || outH > EXPORT_MAX_SIDE) {
    const s = EXPORT_MAX_SIDE / Math.max(outW, outH);
    outW = Math.max(1, Math.round(outW * s));
    outH = Math.max(1, Math.round(outH * s));
  }

  const clonedSvg = svgRef.value.cloneNode(true) as SVGSVGElement;
  stripExportChromeFromSvg(clonedSvg);
  inlineSvgThemeCssVarsForExport(clonedSvg);
  clonedSvg.setAttribute('viewBox', `${viewMinX} ${viewMinY} ${viewW} ${viewH}`);
  clonedSvg.setAttribute('width', String(outW));
  clonedSvg.setAttribute('height', String(outH));
  clonedSvg.removeAttribute('preserveAspectRatio');
  clonedSvg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
  clonedSvg.setAttribute('shape-rendering', 'geometricPrecision');

  try {
    await inlineSvgRasterImages(clonedSvg);
  } catch (e) {
    console.error('[export] inline images', e);
  }

  const svgData = new XMLSerializer().serializeToString(clonedSvg);
  const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
  const svgUrl = URL.createObjectURL(svgBlob);

  const img = new Image();
  img.onload = () => {
    const canvas = document.createElement('canvas');
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      URL.revokeObjectURL(svgUrl);
      window.dispatchEvent(new CustomEvent('canvas-export-done'));
      return;
    }
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.fillStyle = document.body.classList.contains('theme-light') ? '#ffffff' : '#1a1a1a';
    ctx.fillRect(0, 0, outW, outH);
    ctx.drawImage(img, 0, 0, outW, outH);
    canvas.toBlob(
      (blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `hypothesis-${currentHypothesis.value?.id}-${Date.now()}.png`;
          a.click();
          URL.revokeObjectURL(url);
        }
        window.dispatchEvent(new CustomEvent('canvas-export-done'));
      },
      'image/png'
    );
    URL.revokeObjectURL(svgUrl);
  };
  img.onerror = () => {
    URL.revokeObjectURL(svgUrl);
    window.dispatchEvent(new CustomEvent('canvas-export-done'));
  };
  img.src = svgUrl;
}

onMounted(() => {
  updateCanvasSize();
  window.addEventListener('resize', updateCanvasSize);
  window.addEventListener('keydown', handleKeyDown);
  window.addEventListener('frame-block-in-canvas', handleFrameBlock as EventListener);
  window.addEventListener('block-instance-dimensions', handleBlockDimensions as EventListener);
  window.addEventListener('mousemove', handleMouseMove);
  window.addEventListener('mouseup', handleMouseUp);
  window.addEventListener('bloc-drag-start', handleDragEnterCanvas as EventListener);
  window.addEventListener('bloc-drag-end', handleDragLeaveCanvas as EventListener);
  window.addEventListener('annotation-tool-selected', handleAnnotationToolSelected as EventListener);
  window.addEventListener('annotation-selected', handleAnnotationSelected as EventListener);
  window.addEventListener('annotation-edit-requested', handleAnnotationEditRequested as EventListener);
  window.addEventListener('annotations-visibility-changed', handleAnnotationsVisibilityChanged as EventListener);
  window.addEventListener('canvas-export-fitted', handleExportFitted as EventListener);
});

onUnmounted(() => {
  window.removeEventListener('resize', updateCanvasSize);
  window.removeEventListener('keydown', handleKeyDown);
  window.removeEventListener('frame-block-in-canvas', handleFrameBlock as EventListener);
  window.removeEventListener('block-instance-dimensions', handleBlockDimensions as EventListener);
  window.removeEventListener('mousemove', handleMouseMove);
  window.removeEventListener('mouseup', handleMouseUp);
  window.removeEventListener('bloc-drag-start', handleDragEnterCanvas as EventListener);
  window.removeEventListener('bloc-drag-end', handleDragLeaveCanvas as EventListener);
  window.removeEventListener('annotation-tool-selected', handleAnnotationToolSelected as EventListener);
  window.removeEventListener('annotation-selected', handleAnnotationSelected as EventListener);
  window.removeEventListener('annotation-edit-requested', handleAnnotationEditRequested as EventListener);
  window.removeEventListener('annotations-visibility-changed', handleAnnotationsVisibilityChanged as EventListener);
  window.removeEventListener('canvas-export-fitted', handleExportFitted as EventListener);
  if (saveTimeoutId !== null) clearTimeout(saveTimeoutId);
  if (canvasSaveRetryTimeoutId !== null) clearTimeout(canvasSaveRetryTimeoutId);
  if (annotationSaveTimeoutId !== null) clearTimeout(annotationSaveTimeoutId);
  if (annotationRetryTimeoutId !== null) clearTimeout(annotationRetryTimeoutId);
});

watch(
  () => currentHypothesis.value,
  (newHypothesis) => {
    if (newHypothesis) {
      selectedBlockIds.value = [];
      blockDimensionsByBlocId.value = new Map();
    }
  }
);
</script>

<style scoped>
.canvas-viewer {
  flex: 1;
  overflow: auto;
  background-color: var(--cv-canvas-fill);
  position: relative;
}

.canvas-svg {
  display: block;
  width: 100%;
  height: 100%;
}

.canvas-controls {
  position: absolute;
  bottom: 1rem;
  right: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  z-index: 100;
  pointer-events: none; /* Ne pas intercepter les événements de souris */
}

.canvas-controls > * {
  pointer-events: all; /* Réactiver les événements sur les enfants */
}

.zoom-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background-color: rgba(42, 42, 42, 0.9);
  border: 1px solid #444;
  border-radius: 4px;
  padding: 0.5rem;
}

.control-btn {
  width: 32px;
  height: 32px;
  background-color: #333;
  border: 1px solid #444;
  border-radius: 4px;
  color: #e0e0e0;
  cursor: pointer;
  font-size: 1.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.control-btn .material-icons,
.control-btn .material-symbols-outlined {
  font-size: 18px;
}

.control-btn:hover {
  background-color: #444;
  border-color: #4a9eff;
}

.control-btn-disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.group-rotation-handle {
  cursor: grab;
}

.group-rotation-handle:active {
  cursor: grabbing;
}

.zoom-level {
  min-width: 50px;
  text-align: center;
  color: #e0e0e0;
  font-size: 0.9rem;
  font-weight: 500;
}

.pan-hint {
  background-color: rgba(42, 42, 42, 0.9);
  border: 1px solid #444;
  border-radius: 4px;
  padding: 0.5rem 0.75rem;
  color: #999;
  font-size: 0.85rem;
}

.z-order-toggle.active {
  background-color: #4a9eff;
  color: white;
}

.canvas-selection-dims {
  position: absolute;
  bottom: 1rem;
  left: 1rem;
  z-index: 100;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  max-width: min(90vw, 28rem);
  padding: 0.5rem 0.75rem;
  background-color: rgba(42, 42, 42, 0.92);
  border: 1px solid #444;
  border-radius: 4px;
  color: #e0e0e0;
  font-size: 0.85rem;
  line-height: 1.3;
  pointer-events: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.35);
}

.canvas-selection-dims .dims-name {
  font-weight: 600;
  color: #fff;
  word-break: break-word;
}

.canvas-selection-dims .dims-meta {
  font-size: 0.78rem;
  color: #c8c8c8;
  word-break: break-word;
}

.canvas-selection-dims .dims-meta--poids {
  color: #9ec5ff;
}

.canvas-selection-dims .dims-size {
  font-family: ui-monospace, monospace;
  color: #b0c4e8;
  font-size: 0.8rem;
}

.canvas-selection-dims .dims-size--pending {
  color: #888;
  font-style: italic;
}
</style>
